import React, { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import {
  Calendar,
  ArrowRight,
  AlertCircle,
  RefreshCw,
  CheckCircle,
  CreditCard,
  Radio,
  ChevronRight,
} from "lucide-react";
import {
  STATUS_META,
  normalizeStatus,
  getEventPath,
  formatEventDate,
  formatShortDate,
  formatDeadline,
  getPaymentProgress,
  getNextStep,
} from "./vendorDashboard.utils";
import "./VendorDashboardHome.css";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const isAccepted = (status) => status === "approved" || status === "accepted";

const MS_PER_DAY = 86_400_000;
const daysUntil = (date) => Math.ceil((new Date(date) - Date.now()) / MS_PER_DAY);

const formatCountdown = (date) => {
  const days = daysUntil(date);
  if (days <= 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `${days} days`;
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const byStartDate = (a, b) =>
  new Date(a.events?.start_date || 0) - new Date(b.events?.start_date || 0);

const DASHBOARD_APPLICATIONS_SELECT = `
  id, status, tent_type, created_at,
  deposit_submitted, deposit_confirmed,
  balance_submitted, balance_confirmed,
   onboarding_completed,
  events ( id, name, start_date, end_date, location, event_key, status )
`;

/* -------------------------------------------------------------------------- */
/* Small building blocks                                                      */
/* -------------------------------------------------------------------------- */

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status];
  if (!meta) return null;
  return <span className={`vh-badge vh-badge--${meta.tone}`}>{meta.label}</span>;
};

const PaymentProgress = ({ app }) => {
  const { completed, total } = getPaymentProgress(app);
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="vh-progress">
      <div
        className="vh-progress-track"
        role="progressbar"
        aria-valuenow={completed}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label="Onboarding payment progress"
      >
        <div className="vh-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="vh-progress-label">
        {completed} of {total} steps done
      </span>
    </div>
  );
};

/**
 * One card shape for every "do something" item at the top of the page.
 * `tone` drives the left rail colour; content comes in as children.
 */
const ActionCard = ({ tone = "neutral", icon: Icon, title, status, date, cta, children }) => (
  <article className={`vh-action vh-action--${tone}`}>
    <header className="vh-action-head">
      {Icon && <Icon size={18} className="vh-action-icon" aria-hidden="true" />}
      <div className="vh-action-heading">
        <h3 className="vh-action-title">{title}</h3>
        {status && <span className="vh-action-status">{status}</span>}
      </div>
      {date && <span className="vh-action-date">{date}</span>}
    </header>
    {children}
    {cta && (
      <Link to={cta.to} className="vh-btn vh-btn--primary">
        {cta.label}
        <ArrowRight size={15} aria-hidden="true" />
      </Link>
    )}
  </article>
);

const ErrorBanner = ({ message, onRetry }) => (
  <div className="vh-error" role="alert">
    <AlertCircle size={18} aria-hidden="true" />
    <span>{message}</span>
    <button type="button" className="vh-error-retry" onClick={onRetry}>
      <RefreshCw size={14} aria-hidden="true" />
      Retry
    </button>
  </div>
);

/* -------------------------------------------------------------------------- */
/* Page sections                                                              */
/* -------------------------------------------------------------------------- */

const SummaryStrip = ({ items }) => (
  <dl className="vh-summary">
    {items.map((item) => (
      <div key={item.label} className="vh-summary-item">
        <dt>{item.label}</dt>
        <dd>{item.value}</dd>
        {item.hint && <span className="vh-summary-hint">{item.hint}</span>}
      </div>
    ))}
  </dl>
);

/**
 * Hero: one focal event (or a prompt to find one), with the summary stats
 * folded into its footer. `focus` is built in the page from live/next/open state.
 */
const Hero = ({ greeting, dateLabel, focus, stats }) => (
  <section className={`vh-hero vh-hero--${focus.kind}`} aria-labelledby="vh-hero-title">
    <div className="vh-hero-body">
      <div className="vh-hero-copy">
        <p className="vh-hero-greeting">
          {greeting}
          <span className="vh-hero-date">{dateLabel}</span>
        </p>
        {focus.label && <span className="vh-hero-pill">{focus.label}</span>}
        <h1 id="vh-hero-title" className="vh-hero-title">
          {focus.title}
        </h1>
        {focus.meta && <p className="vh-hero-meta">{focus.meta}</p>}
        {focus.cta && (
          <Link to={focus.cta.to} className="vh-btn vh-btn--light">
            {focus.cta.label}
            <ArrowRight size={15} aria-hidden="true" />
          </Link>
        )}
      </div>
      {focus.big && (
        <div className="vh-hero-big" aria-label={`${focus.big.value} ${focus.big.label}`}>
          <span className="vh-hero-big-value">{focus.big.value}</span>
          <span className="vh-hero-big-label">{focus.big.label}</span>
        </div>
      )}
    </div>
    {stats && <SummaryStrip items={stats} />}
  </section>
);

const LiveCard = ({ app }) => {
  const event = app.events;
  const status = normalizeStatus(app.status);
  const accepted = isAccepted(status);

  return (
    <ActionCard
      tone={accepted ? "live" : STATUS_META[status]?.tone}
      icon={accepted ? Radio : STATUS_META[status]?.icon || CheckCircle}
      title={event?.name || "Event"}
      status={accepted ? "Happening now" : STATUS_META[status]?.label}
      date={event?.start_date && formatEventDate(event.start_date)}
      cta={accepted ? { to: getEventPath(event), label: "Open workspace" } : null}
    >
      <p className="vh-action-body">
        {accepted
          ? "The event is live. Your workspace has your spot, documents and payment status."
          : "The event is live and your application is still under review. We'll notify you once there's a decision."}
      </p>
    </ActionCard>
  );
};

const HoldCard = ({ app }) => {
  const event = app.events;
  return (
    <ActionCard
      tone="on_hold"
      icon={STATUS_META.on_hold?.icon || AlertCircle}
      title={event?.name || "Event"}
      status="On hold"
      cta={{ to: getEventPath(event), label: "View details" }}
    >
      <p className="vh-action-body">
        Your place was put on hold because a payment deadline was missed. Open the event to see what's needed to
        restore it.
      </p>
    </ActionCard>
  );
};

const OpenEventCard = ({ event }) => (
  <ActionCard
    tone="open"
    icon={Calendar}
    title={event.name}
    status="Applications open"
    date={event.applications_close_at && `Closes ${formatShortDate(event.applications_close_at)}`}
    cta={{ to: `/vendor/apply/${event.event_key || event.id}`, label: "Apply now" }}
  >
    <p className="vh-action-body">Applications for {event.name} are open. Apply to be considered for a spot.</p>
  </ActionCard>
);

const NextStepCard = ({ app }) => {
  const event = app.events;
  const step = getNextStep(app);
  return (
    <ActionCard
      tone="approved"
      icon={CreditCard}
      title={event?.name || "Event"}
      status={`Next step: ${step.title}`}
      date={event?.start_date && formatEventDate(event.start_date)}
      cta={{ to: getEventPath(event), label: step.actionLabel }}
    >
      <p className="vh-action-body">{step.description}</p>
      <PaymentProgress app={app} />
      {step.deadline && (
        <p className="vh-deadline">
          Due by {formatDeadline(step.deadline)}. After that your spot may be released.
        </p>
      )}
    </ActionCard>
  );
};

const ApplicationRow = ({ app, past = false }) => {
  const event = app.events;
  const status = normalizeStatus(app.status);
  const linked = !past && (isAccepted(status) || status === "on_hold");

  const content = (
    <>
      <div className="vh-row-main">
        <span className="vh-row-name">{event?.name || "Event"}</span>
        <span className="vh-row-meta">
          {event?.start_date ? formatEventDate(event.start_date) : "Date to be announced"}
          {event?.location ? `, ${event.location}` : ""}
        </span>
      </div>
      <StatusBadge status={status} />
      {linked && <ChevronRight size={16} className="vh-row-chevron" aria-hidden="true" />}
    </>
  );

  return linked ? (
    <Link to={getEventPath(event)} className="vh-row vh-row--link">
      {content}
    </Link>
  ) : (
    <div className="vh-row">{content}</div>
  );
};

const UpdateItem = ({ item }) => (
  <Link to={`/vendor/updates/${item.id}`} className="vh-update">
    <img
      src={item.image_url || "/vendor/placeholder-update.jpg"}
      alt=""
      className="vh-update-image"
      loading="lazy"
    />
    <div className="vh-update-text">
      <span className="vh-update-meta">
        <span className="vh-update-type">{item.type}</span>
        {item.start_date && <span>{formatShortDate(item.start_date)}</span>}
      </span>
      <span className="vh-update-title">{item.title}</span>
      {item.description && <span className="vh-update-desc">{item.description}</span>}
    </div>
    <ChevronRight size={16} className="vh-row-chevron" aria-hidden="true" />
  </Link>
);

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

const VendorDashboard = () => {
  const { vendor } = useOutletContext() || {};
  const [applications, setApplications] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [openEvents, setOpenEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!vendor?.id) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      const [appsRes, updatesRes, openRes] = await Promise.all([
        supabase
          .from("vendor_applications")
          .select(DASHBOARD_APPLICATIONS_SELECT)
          .eq("vendor_id", vendor.id)
          .order("created_at", { ascending: false }),
        supabase
          .from("vendor_updates")
          .select("*")
          .eq("is_published", true)
          .gte("start_date", new Date().toISOString())
          .order("start_date", { ascending: true })
          .limit(5),
        supabase
          .from("events")
          .select("id, name, event_key, start_date, applications_close_at")
          .eq("status", "applications_open")
          .order("start_date", { ascending: true }),
      ]);

      if (cancelled) return;

      // Applications are the core data; without them the page is not useful.
      if (appsRes.error) {
        console.error("Failed to load applications:", appsRes.error);
        setError("We couldn't load your dashboard. Check your connection and try again.");
        setLoading(false);
        return;
      }

      // Updates and open events are supplementary; degrade quietly.
      if (updatesRes.error) console.error("Failed to load vendor updates:", updatesRes.error);
      if (openRes.error) console.error("Failed to load open events:", openRes.error);

      setApplications(appsRes.data || []);
      setUpdates(updatesRes.error ? [] : updatesRes.data || []);
      setOpenEvents(openRes.error ? [] : openRes.data || []);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [vendor?.id, reloadToken]);

  if (loading) {
    return (
      <div className="vh-loading" role="status">
        <div className="vh-spinner" aria-hidden="true" />
        <span>Loading your dashboard…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vh-page">
        <ErrorBanner message={error} onRetry={() => setReloadToken((n) => n + 1)} />
      </div>
    );
  }

  /* ---- Derived state ---------------------------------------------------- */

  const now = new Date();
  const activeApps = applications
    .filter((a) => a.events?.status !== "completed")
    .map((a) => ({ ...a, _status: normalizeStatus(a.status) }));

  const liveApp = activeApps.find((a) => a.events?.status === "live") || null;
  const holdApps = activeApps.filter((a) => a._status === "on_hold" && a.id !== liveApp?.id);

  const appliedEventIds = new Set(activeApps.map((a) => a.events?.id).filter(Boolean));
  const unappliedOpenEvents = openEvents.filter((e) => !appliedEventIds.has(e.id));

  const acceptedUpcoming = activeApps
    .filter(
      (a) =>
        isAccepted(a._status) &&
        a.id !== liveApp?.id &&
        a.events?.start_date &&
        new Date(a.events.start_date) >= now
    )
    .sort(byStartDate);
  const nextStepApp = acceptedUpcoming[0] || null;

  const pastApps = applications
    .filter((a) => a.events?.status === "completed")
    .sort((a, b) => byStartDate(b, a))
    .slice(0, 5);

  const eventMeta = (event) =>
    [event?.start_date && formatEventDate(event.start_date), event?.location].filter(Boolean).join(", ");

  const heroFocus = (() => {
    if (liveApp && isAccepted(liveApp._status)) {
      const event = liveApp.events;
      return {
        kind: "live",
        label: "Happening now",
        title: event?.name || "Your event",
        meta: eventMeta(event),
        big: { value: "Live", label: "right now" },
        cta: { to: getEventPath(event), label: "Open workspace" },
      };
    }
    if (nextStepApp) {
      const event = nextStepApp.events;
      const days = daysUntil(event.start_date);
      return {
        kind: "upcoming",
        label: "Your next event",
        title: event.name,
        meta: eventMeta(event),
        big: days <= 0 ? { value: "Today", label: "is the day" } : { value: days, label: days === 1 ? "day to go" : "days to go" },
        cta: { to: getEventPath(event), label: "View event" },
      };
    }
    if (unappliedOpenEvents[0]) {
      const event = unappliedOpenEvents[0];
      const closes = event.applications_close_at ? daysUntil(event.applications_close_at) : null;
      return {
        kind: "open",
        label: "Applications open",
        title: event.name,
        meta: closes != null ? `Applications close ${formatShortDate(event.applications_close_at)}` : eventMeta(event),
        big: closes != null && closes > 0 ? { value: closes, label: closes === 1 ? "day to apply" : "days to apply" } : null,
        cta: { to: `/vendor/apply/${event.event_key || event.id}`, label: "Apply now" },
      };
    }
    return {
      kind: "idle",
      title: "Find your next event",
      meta: "You have nothing scheduled. Browse upcoming events and apply to the ones that suit your brand.",
      cta: { to: "/vendor/events", label: "Browse events" },
    };
  })();

  // The hero already covers an accepted live event and the first open event,
  // so don't repeat them as cards below.
  const heroOpenId = heroFocus.kind === "open" ? unappliedOpenEvents[0]?.id : null;
  const cardOpenEvents = unappliedOpenEvents.filter((e) => e.id !== heroOpenId);
  const showLiveCard = Boolean(liveApp) && !isAccepted(liveApp._status);

  const hasFocusItems =
    showLiveCard || holdApps.length > 0 || cardOpenEvents.length > 0 || Boolean(nextStepApp);

  const sortedApps = [...activeApps].sort((a, b) => {
    if (a.id === liveApp?.id) return -1;
    if (b.id === liveApp?.id) return 1;
    return byStartDate(a, b);
  });

  const nextEventStat = (() => {
    if (liveApp && isAccepted(liveApp._status)) {
      return { value: "Live now", hint: liveApp.events?.name };
    }
    if (nextStepApp) {
      return { value: formatCountdown(nextStepApp.events.start_date), hint: nextStepApp.events.name };
    }
    return { value: "None", hint: "No upcoming events" };
  })();

  const summary = [
    { label: "Applications", value: activeApps.length, hint: "Current and upcoming" },
    { label: "Accepted", value: activeApps.filter((a) => isAccepted(a._status)).length },
    { label: "Under review", value: activeApps.filter((a) => a._status === "pending").length },
    { label: "Next event", ...nextEventStat },
  ];

  /* ---- Render ----------------------------------------------------------- */

  return (
    <div className="vh-page">
      <Hero
        greeting={`${getGreeting()}${vendor?.brand_name ? `, ${vendor.brand_name}` : ""}`}
        dateLabel={now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
        focus={heroFocus}
        stats={activeApps.length > 0 ? summary : null}
      />

      <div className="vh-layout">
        <div className="vh-main">
          {hasFocusItems && (
            <section className="vh-section" aria-labelledby="vh-focus-title">
              <h2 id="vh-focus-title" className="vh-section-title">
                Needs your attention
              </h2>
              <div className="vh-stack">
                {showLiveCard && <LiveCard app={liveApp} />}
                {holdApps.map((app) => (
                  <HoldCard key={app.id} app={app} />
                ))}
                {nextStepApp && <NextStepCard app={nextStepApp} />}
                {cardOpenEvents.map((event) => (
                  <OpenEventCard key={event.id} event={event} />
                ))}
              </div>
            </section>
          )}

          {sortedApps.length > 0 && (
            <section className="vh-section" aria-labelledby="vh-apps-title">
              <h2 id="vh-apps-title" className="vh-section-title">
                Your applications
              </h2>
              <div className="vh-list">
                {sortedApps.map((app) => (
                  <ApplicationRow key={app.id} app={app} />
                ))}
              </div>
            </section>
          )}

          {pastApps.length > 0 && (
            <section className="vh-section" aria-labelledby="vh-past-title">
              <h2 id="vh-past-title" className="vh-section-title">
                Past events
              </h2>
              <div className="vh-list">
                {pastApps.map((app) => (
                  <ApplicationRow key={app.id} app={app} past />
                ))}
              </div>
            </section>
          )}

          {/* Analytics (profile views, application funnel, etc.) will slot in here. */}
        </div>

        <aside className="vh-side">
          {updates.length > 0 && (
            <section className="vh-section" aria-labelledby="vh-updates-title">
              <h2 id="vh-updates-title" className="vh-section-title">
                Coming up
              </h2>
              <div className="vh-list">
                {updates.map((item) => (
                  <UpdateItem key={item.id} item={item} />
                ))}
              </div>
            </section>
          )}

          <section className="vh-section" aria-labelledby="vh-links-title">
            <h2 id="vh-links-title" className="vh-section-title">
              Quick links
            </h2>
            <div className="vh-list">
              <Link to="/vendor/account" className="vh-link-row">
                <span className="vh-link-title">Update your profile</span>
                <span className="vh-link-desc">Description, photos, contacts and KRA PIN</span>
              </Link>
              <Link to="/vendor/events" className="vh-link-row">
                <span className="vh-link-title">Browse events</span>
                <span className="vh-link-desc">Apply for upcoming events</span>
              </Link>
              <div className="vh-link-row vh-link-row--disabled" aria-disabled="true">
                <span className="vh-link-title">Marketplace</span>
                <span className="vh-link-desc">Coming soon</span>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default VendorDashboard;